import { useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import Add from '@mui/icons-material/Add'
import ChevronRight from '@mui/icons-material/ChevronRight'
import Close from '@mui/icons-material/Close'
import EditOutlined from '@mui/icons-material/EditOutlined'
import ExpandMore from '@mui/icons-material/ExpandMore'
import {
  buildUnitTree,
  changeUnitParent,
  codigoDoNome,
  codigoValido,
  createUnit,
  fetchAllUnits,
  idsDaSubarvore,
  TooManyUnitsError,
  UNIT_WRITE_PERMISSION,
  unitSaveError,
  UNITS_QUERY_KEY,
  updateUnitBasics,
  type Unit,
  type UnitBasicsPatch,
  type UnitNode,
  type UnitType,
} from '../api/units'
import { ApiError } from '../api/errors'
import { fetchMe, ME_QUERY_KEY } from '../api/session'
import { ErrorState } from '../components/ErrorState'
import { LoadingState } from '../components/LoadingState'
import { OrgChartView } from '../components/OrgChartView'
import { t } from '../i18n'

/**
 * Tela 3 (sessao 35) - unidades. Regua visual (T11/T12): arvore a esquerda com recuo por nivel e
 * abrir/fechar; clique na linha abre o PAINEL LATERAL a direita (padrao Stripe).
 *
 * s56 / R3 (decisao de Marcelo 14/10): quem tem organization_core.unit.write CRIA, RENOMEIA e escolhe a
 * UNIDADE-MAE no proprio painel (D1); o codigo e GERADO do nome (D2); tipo padrao Area (D3); apagar/inativar
 * fica fora (D4: a API nao tem). A lista de maes exclui a propria unidade e as que estao abaixo dela (o ciclo
 * nem aparece); a API recusa de novo (409 would_create_hierarchy_cycle). O painel e UM so elemento nos tres
 * modos (ver / editar / criar) -- trocar de modo nao desmonta o painel.
 *
 * s61 / E2: chave [Lista | Organograma]. A LISTA continua sendo o padrao (cadastro e edicao moram nela); o
 * ORGANOGRAMA so le, e so busca os dados quando escolhido (/api/org-chart).
 */

const RECUO_POR_NIVEL = 3
const TIPOS: UnitType[] = ['directorate', 'department', 'area', 'team', 'other']
const TIPO_PADRAO: UnitType = 'area'

type Modo = { tipo: 'ver'; id: string } | { tipo: 'editar'; id: string } | { tipo: 'criar' } | null

function Linha({
  node,
  abertos,
  alternar,
  selecionada,
  selecionar,
}: {
  node: UnitNode
  abertos: Set<string>
  alternar: (id: string) => void
  selecionada: string | null
  selecionar: (id: string) => void
}) {
  const temFilhos = node.children.length > 0
  const aberto = abertos.has(node.unit.id)
  return (
    <>
      <ListItemButton
        selected={selecionada === node.unit.id}
        onClick={() => selecionar(node.unit.id)}
        sx={{ pl: 1 + node.depth * RECUO_POR_NIVEL, py: 0.5 }}
        data-testid={`unit-${node.unit.id}`}
      >
        {temFilhos ? (
          <IconButton
            size="small"
            aria-label={`${aberto ? t.units.collapse : t.units.expand} ${node.unit.name}`}
            onClick={(e) => {
              e.stopPropagation()
              alternar(node.unit.id)
            }}
          >
            {aberto ? <ExpandMore fontSize="small" /> : <ChevronRight fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 34 }} />
        )}
        <ListItemText
          primary={node.unit.name}
          secondary={t.units.types[node.unit.unitType]}
          primaryTypographyProps={{ variant: 'body2' }}
          secondaryTypographyProps={{ variant: 'caption' }}
        />
        {node.unit.status !== 'active' && (
          <Chip size="small" variant="outlined" label={t.units.statuses[node.unit.status]} />
        )}
      </ListItemButton>
      {temFilhos &&
        aberto &&
        node.children.map((c) => (
          <Linha
            key={c.unit.id}
            node={c}
            abertos={abertos}
            alternar={alternar}
            selecionada={selecionada}
            selecionar={selecionar}
          />
        ))}
    </>
  )
}

function Campo({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" component="dt">
        {rotulo}
      </Typography>
      <Typography variant="body2" component="dd" sx={{ m: 0 }}>
        {valor}
      </Typography>
    </Box>
  )
}

/** Casca unica do painel: o MESMO elemento nos tres modos (os testes seguram a referencia). */
function Casca({ titulo, fechar, acoes, children }: { titulo: string; fechar: () => void; acoes?: ReactNode; children: ReactNode }) {
  return (
    <Paper
      component="aside"
      aria-label={titulo}
      variant="outlined"
      // s56c: acompanha a rolagem -- unidade no fim da lista nao obriga a subir para editar (pedido de Marcelo).
      sx={{ width: { xs: '100%', md: 340 }, flexShrink: 0, p: 2, alignSelf: 'flex-start', position: 'sticky', top: 16, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h2" component="h2" sx={{ fontSize: '1.125rem' }}>
          {titulo}
        </Typography>
        <Stack direction="row" spacing={0.5}>
          {acoes}
          <IconButton size="small" aria-label={t.units.panelClose} onClick={fechar}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      {children}
    </Paper>
  )
}

function Detalhes({ unit, mae }: { unit: Unit; mae: Unit | null }) {
  return (
    <Stack component="dl" spacing={1.5} sx={{ m: 0 }}>
      <Campo rotulo={t.units.fieldCode} valor={unit.code} />
      <Campo rotulo={t.units.fieldName} valor={unit.name} />
      <Campo rotulo={t.units.fieldShortName} valor={unit.shortName ?? t.units.none} />
      <Campo rotulo={t.units.fieldType} valor={t.units.types[unit.unitType]} />
      <Campo rotulo={t.units.fieldStatus} valor={t.units.statuses[unit.status]} />
      <Campo rotulo={t.units.fieldParent} valor={mae?.name ?? t.units.none} />
    </Stack>
  )
}

function mensagem(e: unknown): string {
  const k = unitSaveError(e)
  if (k === 'duplicate') return t.units.errDuplicate
  if (k === 'cycle') return t.units.errCycle
  if (k === 'parent') return t.units.errParent
  return t.units.errGeneric
}

/** E5-s56: o erro mostra o codigo de rastreio (ADR-0024 D9.3), como o ErrorState. */
function ErroSalvar({ e }: { e: unknown }) {
  const id = e instanceof ApiError ? e.requestId : null
  return (
    <Alert severity="error">
      {mensagem(e)}
      {id && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" component="div">
            {t.error.requestIdHint}
          </Typography>
          <Typography variant="caption" component="div" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {id}
          </Typography>
        </Box>
      )}
    </Alert>
  )
}

function SeletorTipo({ id, valor, mudar }: { id: string; valor: UnitType; mudar: (v: UnitType) => void }) {
  return (
    <TextField
      id={id}
      select
      size="small"
      label={t.units.fieldType}
      value={valor}
      onChange={(e) => mudar(e.target.value as UnitType)}
      slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
    >
      {TIPOS.map((tp) => (
        <option key={tp} value={tp}>
          {t.units.types[tp]}
        </option>
      ))}
    </TextField>
  )
}

function SeletorMae({ id, valor, mudar, opcoes }: { id: string; valor: string; mudar: (v: string) => void; opcoes: Unit[] }) {
  return (
    <TextField
      id={id}
      select
      size="small"
      label={t.units.fieldParent}
      value={valor}
      onChange={(e) => mudar(e.target.value)}
      slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
    >
      <option value="">{t.units.noParent}</option>
      {opcoes.map((u) => (
        <option key={u.id} value={u.id}>
          {u.name}
        </option>
      ))}
    </TextField>
  )
}

const porNome = (a: Unit, b: Unit) => a.name.localeCompare(b.name, 'pt-BR')

function FormCriar({ unidades, salvo, cancelar }: { unidades: Unit[]; salvo: (id: string) => void; cancelar: () => void }) {
  const qc = useQueryClient()
  const [nome, setNome] = useState('')
  const [codigo, setCodigo] = useState('')
  const [codigoManual, setCodigoManual] = useState(false)
  const [tipo, setTipo] = useState<UnitType>(TIPO_PADRAO)
  const [mae, setMae] = useState('')
  const criar = useMutation({
    mutationFn: () => createUnit({ code: codigo, name: nome.trim(), unitType: tipo, parentId: mae === '' ? null : mae }),
    onSuccess: (id) => {
      void qc.invalidateQueries({ queryKey: UNITS_QUERY_KEY })
      salvo(id)
    },
  })
  const opcoes = useMemo(() => [...unidades].sort(porNome), [unidades])
  const pode = nome.trim() !== '' && codigoValido(codigo) && !criar.isPending
  return (
    <Stack spacing={2}>
      <TextField
        id="unit-new-name"
        size="small"
        label={t.units.fieldName}
        value={nome}
        onChange={(e) => {
          setNome(e.target.value)
          if (!codigoManual) setCodigo(codigoDoNome(e.target.value))
        }}
        slotProps={{ htmlInput: { maxLength: 255 } }}
      />
      <TextField
        id="unit-new-code"
        size="small"
        label={t.units.fieldCode}
        value={codigo}
        onChange={(e) => {
          setCodigoManual(true)
          setCodigo(e.target.value)
        }}
        error={codigo !== '' && !codigoValido(codigo)}
        helperText={codigo !== '' && !codigoValido(codigo) ? t.units.codeInvalid : t.units.codeHelp}
        slotProps={{ htmlInput: { maxLength: 64 } }}
      />
      <SeletorTipo id="unit-new-type" valor={tipo} mudar={setTipo} />
      <SeletorMae id="unit-new-parent" valor={mae} mudar={setMae} opcoes={opcoes} />
      {criar.isError && <ErroSalvar e={criar.error} />}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={!pode} onClick={() => criar.mutate()}>
          {t.units.save}
        </Button>
        <Button onClick={cancelar}>{t.units.cancel}</Button>
      </Stack>
    </Stack>
  )
}

function FormEditar({ unit, unidades, pronto }: { unit: Unit; unidades: Unit[]; pronto: () => void }) {
  const qc = useQueryClient()
  const [nome, setNome] = useState(unit.name)
  const [curto, setCurto] = useState(unit.shortName ?? '')
  const [tipo, setTipo] = useState<UnitType>(unit.unitType)
  const [mae, setMae] = useState(unit.parentId ?? '')
  const opcoes = useMemo(() => {
    const fora = idsDaSubarvore(unidades, unit.id)
    return unidades.filter((u) => !fora.has(u.id)).sort(porNome)
  }, [unidades, unit.id])
  const salvar = useMutation({
    mutationFn: async () => {
      const patch: UnitBasicsPatch = {}
      if (nome.trim() !== unit.name) patch.name = nome.trim()
      const curtoNovo = curto.trim() === '' ? null : curto.trim()
      if (curtoNovo !== unit.shortName) patch.short_name = curtoNovo
      if (tipo !== unit.unitType) patch.unit_type = tipo
      if (Object.keys(patch).length > 0) await updateUnitBasics(unit.id, patch)
      const maeNova = mae === '' ? null : mae
      if (maeNova !== unit.parentId) await changeUnitParent(unit.id, maeNova)
    },
    onSettled: () => void qc.invalidateQueries({ queryKey: UNITS_QUERY_KEY }),
    onSuccess: pronto,
  })
  const pode = nome.trim() !== '' && !salvar.isPending
  return (
    <Stack spacing={2}>
      <TextField
        id="unit-edit-name"
        size="small"
        label={t.units.fieldName}
        value={nome}
        onChange={(e) => setNome(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 255 } }}
      />
      <TextField
        id="unit-edit-short"
        size="small"
        label={t.units.fieldShortName}
        value={curto}
        onChange={(e) => setCurto(e.target.value)}
        slotProps={{ htmlInput: { maxLength: 64 } }}
      />
      <SeletorTipo id="unit-edit-type" valor={tipo} mudar={setTipo} />
      <SeletorMae id="unit-edit-parent" valor={mae} mudar={setMae} opcoes={opcoes} />
      <Campo rotulo={t.units.fieldCode} valor={unit.code} />
      {salvar.isError && <ErroSalvar e={salvar.error} />}
      <Stack direction="row" spacing={1}>
        <Button variant="contained" disabled={!pode} onClick={() => salvar.mutate()}>
          {t.units.save}
        </Button>
        <Button onClick={pronto}>{t.units.cancel}</Button>
      </Stack>
    </Stack>
  )
}

export function UnitsPage() {
  const q = useQuery({ queryKey: UNITS_QUERY_KEY, queryFn: fetchAllUnits })
  const me = useQuery({ queryKey: ME_QUERY_KEY, queryFn: fetchMe })
  const podeEscrever = (me.data?.permissions ?? []).includes(UNIT_WRITE_PERMISSION)
  const unidades = useMemo(() => q.data?.units ?? [], [q.data])
  const nInvalidas = q.data?.invalid.length ?? 0
  const arvore = useMemo(() => buildUnitTree(unidades), [unidades])
  const porId = useMemo(() => new Map(unidades.map((u) => [u.id, u])), [unidades])
  const [fechados, setFechados] = useState<Set<string>>(new Set())
  const [modo, setModo] = useState<Modo>(null)
  const [visao, setVisao] = useState<'lista' | 'organograma' | 'areas'>('lista')

  const abertos = useMemo(
    () => new Set(unidades.map((u) => u.id).filter((id) => !fechados.has(id))),
    [unidades, fechados],
  )
  function alternar(id: string) {
    setFechados((prev) => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id)
      else n.add(id)
      return n
    })
  }

  const selecionada = modo && modo.tipo !== 'criar' ? modo.id : null
  const unidade = selecionada ? (porId.get(selecionada) ?? null) : null
  const mae = unidade?.parentId ? (porId.get(unidade.parentId) ?? null) : null
  const fechar = () => setModo(null)

  let painel: ReactNode = null
  if (modo?.tipo === 'criar') {
    painel = (
      <Casca titulo={t.units.newUnit} fechar={fechar}>
        <FormCriar unidades={unidades} salvo={(id) => setModo({ tipo: 'ver', id })} cancelar={fechar} />
      </Casca>
    )
  } else if (unidade && modo) {
    const acoes =
      podeEscrever && modo.tipo === 'ver' ? (
        <IconButton size="small" aria-label={t.units.edit} onClick={() => setModo({ tipo: 'editar', id: unidade.id })}>
          <EditOutlined fontSize="small" />
        </IconButton>
      ) : null
    painel = (
      <Casca titulo={unidade.name} fechar={fechar} acoes={acoes}>
        {modo.tipo === 'editar' ? (
          <FormEditar key={unidade.id} unit={unidade} unidades={unidades} pronto={() => setModo({ tipo: 'ver', id: unidade.id })} />
        ) : (
          <Detalhes unit={unidade} mae={mae} />
        )}
      </Casca>
    )
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="h1" component="h1">
            {t.units.title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t.units.subtitle}
          </Typography>
        </Box>
        {podeEscrever && q.isSuccess && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setVisao('lista') // cadastro mora na lista
              setModo({ tipo: 'criar' })
            }}
          >
            {t.units.newUnit}
          </Button>
        )}
      </Stack>

      {q.isPending && <LoadingState />}
      {q.isError &&
        (q.error instanceof TooManyUnitsError ? (
          <Alert severity="warning">{t.units.tooMany}</Alert>
        ) : (
          <ErrorState error={q.error} onRetry={() => void q.refetch()} />
        ))}
      {q.isSuccess && nInvalidas > 0 && <Alert severity="warning">{t.units.invalidRows(nInvalidas)}</Alert>}
      {q.isSuccess && unidades.length === 0 && nInvalidas === 0 && modo?.tipo !== 'criar' && (
        <Alert severity="info">{t.units.empty}</Alert>
      )}
      {q.isSuccess && unidades.length > 0 && (
        <ToggleButtonGroup
          size="small"
          exclusive
          value={visao}
          onChange={(_e, v: 'lista' | 'organograma' | 'areas' | null) => {
            if (v) setVisao(v)
          }}
          aria-label={t.units.viewLabel}
          sx={{ alignSelf: 'flex-start' }}
        >
          <ToggleButton value="lista">{t.units.viewList}</ToggleButton>
          <ToggleButton value="organograma">{t.units.viewChart}</ToggleButton>
          <ToggleButton value="areas">{t.units.viewAreas}</ToggleButton>
        </ToggleButtonGroup>
      )}
      {q.isSuccess && visao !== 'lista' && unidades.length > 0 && <OrgChartView modo={visao === 'areas' ? 'areas' : 'cargos'} />}
      {q.isSuccess && (visao === 'lista' || unidades.length === 0) && (unidades.length > 0 || modo?.tipo === 'criar') && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
          <Paper variant="outlined" sx={{ flexGrow: 1, minWidth: 0 }}>
            <List dense aria-label={t.units.title} disablePadding>
              {arvore.map((n) => (
                <Linha
                  key={n.unit.id}
                  node={n}
                  abertos={abertos}
                  alternar={alternar}
                  selecionada={selecionada}
                  selecionar={(id) => setModo({ tipo: 'ver', id })}
                />
              ))}
            </List>
          </Paper>
          {painel}
        </Stack>
      )}
    </Stack>
  )
}
